import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronRight, Copy, MapPinned } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'

import { growerAreaQrValue } from './grower-area-qr-value'
import type {
    GrowerAreaQrEntry,
    GrowerAreaQrKind,
    GrowerAreaQrOutcome,
} from '@/lib/grower-area-qr-tracking'
import { captureGrowerAreaQrAction } from '@/lib/grower-area-qr-tracking'
import { publicGrowerAreaUrl } from '@/lib/api/public-grower-area-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { m } from '@/lib/paraglide/messages'

/** Vùng trồng ở dạng tối thiểu mà popup cần — khớp cả `GrowerArea` của API
 *  lẫn vùng đang nằm trên form sửa hộ (vùng mới chưa có `id`). */
export interface GrowerAreaQRItem {
    id?: string
    code?: string
    name?: string
}

export interface GrowerAreaQRDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Vùng trồng của hộ. `undefined` = chưa biết (đang tải hoặc lỗi). */
    areas: GrowerAreaQRItem[] | undefined
    /** Tên khai sinh của hộ, hiện dưới QR để người quét biết vùng của ai. */
    growerName?: string | null
    /**
     * Số điện thoại của hộ — NỬA ĐẦU nội dung mã QR, không phải thông tin trang
     * trí. Thiếu nó thì không dựng được mã (xem `growerAreaQrValue`).
     */
    growerPhone?: string | null
    /** Đang tải hồ sơ hộ — khác hẳn "hộ chưa khai vùng nào". */
    loading?: boolean
    /**
     * Tải hồ sơ hộ thất bại. Bắt buộc là trạng thái riêng: gộp vào nhánh "chưa
     * khai vùng trồng" là nói với hộ rằng hồ sơ của họ thiếu dữ liệu, trong khi
     * thực ra chỉ là hỏng mạng.
     */
    error?: boolean
    onRetry?: () => void
    /**
     * Màn nào đang mở popup. BẮT BUỘC: bốn màn dùng chung đúng một popup, nên
     * thiếu nó thì đo mức dùng không tách được màn nào đang bị chặn. Là prop
     * required để một lối vào mới không lặng lẽ trôi vào nhóm "không rõ".
     */
    entry: GrowerAreaQrEntry
}

/**
 * Popup QR vùng trồng dùng chung cho mọi lối vào (màn chi tiết hộ của nhân
 * viên, màn hộ tự xem, nút trên dashboard tài khoản hộ).
 *
 * Mỗi vùng có HAI mã, chọn bằng tab (M-84):
 *   - **Mã cân chè** (mặc định): SỐ ĐIỆN THOẠI của hộ ghép liền MÃ VÙNG TRỒNG,
 *     không dấu cách (vd `0912345678MVT001`) — xem `growerAreaQrValue`. Cân
 *     điện tử quét mã này, nên mở popup luôn rơi vào tab này.
 *   - **Mã cắm vùng**: đường dẫn trang công khai của vùng, in ra cắm ở thửa để
 *     nông dân quét mở trang vùng và tạo nhật ký chăm sóc. Chỉ cần vùng đã lưu,
 *     không cần SĐT hộ hay mã vùng.
 * Mỗi lần chỉ hiện một mã để nhân viên cân không quét nhầm mã cắm vùng.
 *
 * Việc chọn vùng nằm luôn trong popup chứ không ở nút bấm bên ngoài — có vậy
 * mọi lối vào mới dùng chung được một popup, và hộ nhiều thửa không cần biết
 * mình đang đứng ở màn nào.
 */
export function GrowerAreaQRDialog({
    open,
    onOpenChange,
    areas,
    growerName,
    growerPhone,
    loading = false,
    error = false,
    onRetry,
    entry,
}: GrowerAreaQRDialogProps) {
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [kind, setKind] = useState<QrKind>('scale')

    // Chỉ vùng ĐÃ LƯU mới có trang công khai để trỏ tới. Vùng vừa thêm trên màn
    // sửa chưa có id nên phải loại khỏi danh sách chọn.
    //
    // Nhãn chốt ngay ở đây theo VỊ TRÍ TRONG CẢ DANH SÁCH vùng của hộ: vùng chưa
    // đặt tên lùi về "Vùng N" đúng như tab trên màn hộ. Đánh số lại sau khi lọc
    // là vùng thứ ba không tên hiện thành "Vùng 1", người dùng in QR xong dán
    // nhầm thửa.
    const savedAreas = useMemo(
        () =>
            (areas ?? []).flatMap((a, i) =>
                typeof a.id === 'string' && a.id.trim() !== ''
                    ? [
                          {
                              ...a,
                              id: a.id,
                              label:
                                  a.name?.trim() ||
                                  m.grower_area_tab_fallback({ n: i + 1 }),
                          },
                      ]
                    : [],
            ),
        [areas],
    )
    const hasUnsavedOnly = (areas?.length ?? 0) > 0 && savedAreas.length === 0
    // `undefined` = người gọi CHƯA biết hộ có vùng nào, khác hẳn `[]` = biết rõ
    // là không có. Nói "hộ chưa khai vùng trồng nào" lúc chưa biết là bịa.
    const unknownAreas = areas === undefined

    // Đóng popup là quên lựa chọn: mở lại cho hộ khác (hoặc sau khi sửa vùng)
    // mà vẫn giữ vùng cũ thì QR hiện ra là của vùng không còn liên quan.
    // Tab cũng về mặc định: người cân chè mở popup không được gặp mã cắm vùng
    // do lần trước ai đó để lại.
    useEffect(() => {
        if (!open) {
            setSelectedId(null)
            setKind('scale')
        }
    }, [open])

    // Đúng một vùng thì không có gì để chọn — vào thẳng QR.
    const selected =
        savedAreas.find((a) => a.id === selectedId) ??
        (savedAreas.length === 1 ? savedAreas[0] : null)

    const url = selected ? publicGrowerAreaUrl(selected.id) : ''
    // Rỗng = thiếu số điện thoại hộ hoặc thiếu mã vùng (chỉ mã cân chè);
    // `AreaQRView` nói rõ thiếu phần nào thay vì dựng một mã quét ra dữ liệu
    // dở dang. Mã cắm vùng chính là đường dẫn, có vùng đã lưu là có.
    const qrValue = !selected
        ? ''
        : kind === 'field_link'
          ? url
          : growerAreaQrValue(growerPhone, selected.code)
    const hasPhone = (growerPhone ?? '').trim() !== ''

    // Một discriminator duy nhất cho CẢ phần render lẫn phần đo: hai nhánh điều
    // kiện song song là chuyện màn hình hiện một thứ mà telemetry ghi thứ khác.
    const view: DialogView =
        loading || (unknownAreas && !error)
            ? 'loading'
            : error && unknownAreas
              ? 'load_error'
              : hasUnsavedOnly
                ? 'unsaved_area'
                : savedAreas.length === 0
                  ? 'no_area'
                  : selected
                    ? 'qr'
                    : 'pick'

    // `null` = chưa có kết quả nào để ghi (đang tải). Mọi trạng thái còn lại đều
    // là một kết quả người dùng nhận được, kể cả các kết quả không ra mã.
    const outcome: GrowerAreaQrOutcome | null =
        view === 'loading'
            ? null
            : view === 'pick'
              ? 'pick_area'
              : view === 'qr'
                ? qrValue
                    ? 'code_ready'
                    : hasPhone
                      ? 'missing_area_code'
                      : 'missing_phone'
                : view

    // Tab chỉ tồn tại trên màn mã; kết quả ở mọi màn khác ghi `none`.
    const qrKind: GrowerAreaQrKind = view === 'qr' ? kind : 'none'

    // Ghi đúng một lần cho mỗi kết quả đạt tới trong một lần mở popup: render
    // lại, chuyển qua lại giữa hai tab hay quay lại vùng đã xem không được đếm
    // thêm.
    const reported = useRef(new Set<string>())
    useEffect(() => {
        if (!open) {
            reported.current.clear()
            return
        }
        if (!outcome) return
        const key = `${outcome}:${qrKind}:${selected?.id ?? ''}`
        if (reported.current.has(key)) return
        reported.current.add(key)
        captureGrowerAreaQrAction({
            entry,
            outcome,
            savedAreas: savedAreas.length,
            picked: selectedId !== null,
            qrKind,
        })
    }, [
        open,
        outcome,
        qrKind,
        entry,
        savedAreas.length,
        selected?.id,
        selectedId,
    ])

    async function copyLink() {
        try {
            await navigator.clipboard.writeText(url)
            toast.success(m.toast_copied())
        } catch {
            // Trình duyệt chặn clipboard (không phải HTTPS, người dùng từ chối
            // quyền): đường dẫn vẫn hiện ngay dưới QR để chép tay.
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>
                        {selected
                            ? m.grower_area_qr_title()
                            : m.grower_area_qr_pick_title()}
                    </DialogTitle>
                    <DialogDescription>
                        {!selected
                            ? m.grower_area_qr_pick_subtitle()
                            : kind === 'field_link'
                              ? m.grower_area_qr_field_subtitle()
                              : m.grower_area_qr_subtitle()}
                    </DialogDescription>
                </DialogHeader>

                {view === 'loading' ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                        {m.grower_qr_loading()}
                    </p>
                ) : view === 'load_error' ? (
                    <div className="flex flex-col items-center gap-3 py-4">
                        <p className="text-center text-sm text-muted-foreground">
                            {m.my_grower_load_error()}
                        </p>
                        {onRetry && (
                            <Button type="button" onClick={onRetry}>
                                {m.my_grower_retry()}
                            </Button>
                        )}
                    </div>
                ) : view === 'unsaved_area' ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                        {m.grower_area_qr_unsaved()}
                    </p>
                ) : view === 'no_area' ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                        {m.grower_area_qr_pick_empty()}
                    </p>
                ) : view === 'qr' && selected ? (
                    <AreaQRView
                        area={selected}
                        kind={kind}
                        onKindChange={setKind}
                        url={url}
                        qrValue={qrValue}
                        hasPhone={hasPhone}
                        growerName={growerName}
                        onCopy={() => void copyLink()}
                        onChangeArea={
                            savedAreas.length > 1
                                ? () => setSelectedId(null)
                                : undefined
                        }
                    />
                ) : (
                    <AreaPickList
                        areas={savedAreas}
                        onPick={(id) => setSelectedId(id)}
                    />
                )}
            </DialogContent>
        </Dialog>
    )
}

/**
 * Popup đang ở trạng thái nào. Render và telemetry đọc CÙNG giá trị này, nên
 * không có cửa cho việc màn hình báo một đằng, số đo ghi một nẻo.
 */
type DialogView =
    'loading' | 'load_error' | 'unsaved_area' | 'no_area' | 'qr' | 'pick'

/** Tab đang chọn trên màn mã — cùng giá trị với `qr_kind` gửi analytics. */
type QrKind = Exclude<GrowerAreaQrKind, 'none'>

/** Vùng đã lưu kèm nhãn hiển thị đã chốt (tên vùng, hoặc "Vùng N"). */
type PickableArea = GrowerAreaQRItem & { id: string; label: string }

function AreaPickList({
    areas,
    onPick,
}: {
    areas: PickableArea[]
    onPick: (id: string) => void
}) {
    return (
        <div className="grid max-h-[60vh] grid-cols-1 gap-2 overflow-y-auto">
            {areas.map((a) => (
                <button
                    key={a.id}
                    type="button"
                    onClick={() => onPick(a.id)}
                    className="flex items-center gap-3 rounded-xl border bg-card p-3 text-left text-card-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <MapPinned className="size-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">
                            {a.label}
                        </span>
                        {a.code && (
                            <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
                                {a.code}
                            </span>
                        )}
                    </span>
                    <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                </button>
            ))}
        </div>
    )
}

function AreaQRView({
    area,
    kind,
    onKindChange,
    url,
    qrValue,
    hasPhone,
    growerName,
    onCopy,
    onChangeArea,
}: {
    area: PickableArea
    kind: QrKind
    onKindChange: (kind: QrKind) => void
    url: string
    /** Nội dung mã QR đã chốt; rỗng = chưa dựng được mã. */
    qrValue: string
    /** Hộ có số điện thoại hay không — quyết định báo thiếu SĐT hay thiếu mã vùng. */
    hasPhone: boolean
    growerName?: string | null
    onCopy: () => void
    onChangeArea?: () => void
}) {
    return (
        <div className="flex flex-col items-center gap-3 py-2">
            <Tabs
                value={kind}
                // Radix trả `string`; chỉ có hai tab nên không phải
                // `field_link` thì là `scale`.
                onValueChange={(value) =>
                    onKindChange(
                        value === 'field_link' ? 'field_link' : 'scale',
                    )
                }
                className="w-full"
            >
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="scale">
                        {m.grower_area_qr_tab_scale()}
                    </TabsTrigger>
                    <TabsTrigger value="field_link">
                        {m.grower_area_qr_tab_field()}
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {qrValue ? (
                <>
                    {/* Nền trắng cố định: QR trên nền tối là quét không ra. */}
                    <div className="rounded-xl bg-white p-3">
                        <QRCodeSVG value={qrValue} size={200} />
                    </div>
                    {/* Nội dung mã hiện nguyên văn ngay dưới mã: người dùng đối
                        chiếu được cái máy quét đọc ra với cái đang in trên tem,
                        và không phải đoán xem mã chứa gì. */}
                    <p
                        className={
                            kind === 'field_link'
                                ? 'w-full break-all text-center text-xs text-muted-foreground'
                                : 'w-full break-all text-center text-lg font-semibold tracking-wide'
                        }
                    >
                        {qrValue}
                    </p>
                </>
            ) : (
                /* Thiếu SĐT hộ và thiếu mã vùng là hai việc phải sửa ở hai nơi
                   khác nhau, nên KHÔNG gộp thành một câu chung. */
                <p className="py-4 text-center text-sm text-muted-foreground">
                    {hasPhone
                        ? m.grower_area_qr_no_area_code()
                        : m.grower_area_qr_no_phone()}
                </p>
            )}

            <div className="w-full space-y-0.5 text-center">
                <p className="text-base font-semibold">{area.label}</p>
                {area.code && (
                    <p className="font-mono text-sm text-muted-foreground">
                        {area.code}
                    </p>
                )}
                {growerName && (
                    <p className="text-sm text-muted-foreground">
                        {growerName}
                    </p>
                )}
            </div>

            {/* Đường dẫn hiện nguyên văn: máy quét hỏng, mạng chặn clipboard hay
                in ra giấy thì người dùng vẫn gõ lại được. Tab mã cắm vùng đã
                hiện đường dẫn ngay dưới mã nên không lặp lại. */}
            {kind === 'scale' && (
                <p className="w-full break-all text-center text-xs text-muted-foreground">
                    {url}
                </p>
            )}

            <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onCopy}
                >
                    <Copy className="size-4" />
                    {m.grower_area_qr_copy_link()}
                </Button>
                {onChangeArea && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onChangeArea}
                    >
                        {m.grower_area_qr_change_area()}
                    </Button>
                )}
            </div>
        </div>
    )
}
